import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check, ExternalLink } from "lucide-react";

async function copyText(text) {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Clipboard permission refused or the page is not focused: use the fallback below.
    }
  }
  // Also the only way on http:// pages (local network testing), which have no Clipboard API
  const el = document.createElement("textarea");
  el.value = text;
  el.setAttribute("readonly", "");
  el.style.position = "fixed";
  el.style.opacity = "0";
  document.body.appendChild(el);
  el.select();
  const copied = document.execCommand("copy");
  el.remove();
  if (!copied) throw new Error("copy failed");
}

/**
 * A read-only value with its own Copy button.
 * Props: label, value, testid, href (adds an "open" link), hint
 */
export default function CopyField({ label, value, testid = "copy-field", href, hint }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await copyText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Could not copy — select the text and copy it manually");
    }
  };

  return (
    <div data-testid={testid}>
      <div className="admin-overline">{label}</div>
      <div className="mt-1 flex items-stretch gap-2">
        <div className="flex-1 min-w-0 border border-neutral-300 bg-neutral-50 px-3 py-2.5 text-sm font-mono break-anywhere select-all" data-testid={`${testid}-value`}>
          {value || "—"}
        </div>
        {href && (
          <a href={href} target="_blank" rel="noreferrer" title={`Open ${label}`} aria-label={`Open ${label}`}
            className="icon-btn border border-neutral-300 bg-white hover:bg-neutral-50 px-3 flex-shrink-0">
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
        <button type="button" onClick={copy} disabled={!value} data-testid={`${testid}-copy`}
          className="flex-shrink-0 bg-black text-white px-3 text-xs font-semibold uppercase tracking-wider inline-flex items-center gap-1.5 hover:bg-neutral-800 disabled:opacity-40 min-h-[44px]">
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copied" : "Copy"}
        </button>
      </div>
      {hint && <div className="text-[11px] text-neutral-500 mt-1">{hint}</div>}
    </div>
  );
}
